import type { Tracking } from "@otto-ec/tracking-bct";
import { mergeParameterValues } from "@gr-exports/async/attribute-parser";
import type { ParametersParserOptions } from "../../attribute-parser/options";

/**
 *
 */
export const dataContainerParam = "dataContainer";

/**
 *
 */
export const primitiveParams = [
  "mergeId",
  "onLate",
  "id",
  "url",
  "method",
  "track",
  "lockInit",
] as const;

/**
 *
 */
export const booleanParams = ["once"] as const;

/**
 *
 */
export const arrayParams = ["features", "updates", "featuresBlocked"] as const;

/**
 *
 */
export const objectParams = [dataContainerParam, "action", "options", "observer"] as const;

export const allParams = [...primitiveParams, ...objectParams, ...arrayParams] as const;

export const parserParametersOptions: ParametersParserOptions<TrackingParametersMap> =
  Object.fromEntries([
    ...arrayParams.map((p) => [p, { array: true }]),
    ...primitiveParams.map((p) => [p, { primitive: true }]),
    ...booleanParams.map((p) => [p, { boolean: true }]),
    ...objectParams.map((p) => [p, { object: true }]),
  ] as const);

/**
 *
 */
export type RecursiveObjectOrValue =
  | string
  | number
  | undefined
  | null
  | { [key: string | number]: RecursiveObjectOrValue }
  | RecursiveObjectOrValue[];

/**
 *
 *
 */
export type TrackingParametersMap = {
  [P in (typeof objectParams)[number]]?: Record<string, RecursiveObjectOrValue>;
} & {
  [P in (typeof arrayParams)[number]]?: RecursiveObjectOrValue[];
} & {
  [P in (typeof primitiveParams)[number]]?: string;
} & {
  [P in (typeof booleanParams)[number]]?: string;
};

type AsParamNames<T> = {
  [P in keyof T]: NonNullable<T[P]> extends Array<unknown>
    ? (typeof arrayParams)[number]
    : NonNullable<T[P]> extends object
      ? (typeof objectParams)[number]
      : (typeof primitiveParams)[number];
};
/**
 *
 */
export type ParameterMap = { [P in keyof Tracking]: AsParamNames<Parameters<Tracking[P]>> };

export type MappedParameters = {
  -readonly [P in keyof typeof parameterMap]: (typeof parameterMap)[P];
};

export type MapMethodParams<M extends keyof Tracking> = {
  [P in keyof Parameters<Tracking[M]> as P extends number
    ? never
    : P extends keyof MappedParameters[M]
      ? MappedParameters[M][P] extends string
        ? MappedParameters[M][P]
        : never
      : never]: Parameters<Tracking[M]>[P];
};

/**
 *
 */
export type DataTrMethodParameterMap = {
  [M in keyof Tracking]: MapMethodParams<M>;
};

/**
 *
 *
 *
 *
 */
export const parameterMap = {
  createPage: [primitiveParams[3], dataContainerParam],
  submitEvent: [objectParams[0], objectParams[1]],
  submitEventMerge: [primitiveParams[0], dataContainerParam, objectParams[1]],
  submitMerge: [dataContainerParam, arrayParams[0], primitiveParams[1]],
  submitMove: [dataContainerParam, objectParams[1]],
  submitMiniAction: [arrayParams[1]],
  createEventMergeContext: [],
  trackOnNextPageImpression: [dataContainerParam],
  createContext: [primitiveParams[2], primitiveParams[3], dataContainerParam],
  replaceContext: [primitiveParams[2], primitiveParams[3], dataContainerParam],
  closeContext: [],
  getPageMergeId: [],
  trackBlockedFeatures: [arrayParams[2]],
} as const satisfies ParameterMap;

/**
 *
 *
 *
 *
 *
 *
 */
export function getPropByName(
  obj: Record<string, RecursiveObjectOrValue>,
  name: string,
): RecursiveObjectOrValue {
  return obj[name] ?? obj[name.toLowerCase()];
}

/**
 *
 *
 *
 *
 *
 *
 */
export function buildParameters<T extends keyof Tracking>(
  method: T,
  eventParams: Record<string, RecursiveObjectOrValue>,
  options: Record<string, RecursiveObjectOrValue>,
): [
  parameterList: Parameters<Tracking[T]>,
  parameterMap: Record<string, Parameters<Tracking[T]>[number]>,
] {
  const paramNames = parameterMap[method];

  /*                                     */
  const parameterL = [] as RecursiveObjectOrValue[];
  /*                                    */
  const parameterM = {} as Record<string, RecursiveObjectOrValue>;

  /*                                                              */
  paramNames.forEach((key) => {
    const eventValue = getPropByName(eventParams, key);
    const optionValue = getPropByName(options, key);

    if (!eventValue && !optionValue) {
      parameterL.push(undefined);
      parameterM[key] = undefined;
      return;
    }

    /*                                                                */
    const merged = mergeParameterValues(parserParametersOptions[key], eventValue, optionValue);

    /*                      */
    parameterL.push(merged);
    parameterM[key] = merged;
  });

  /*                                                            */
  return [parameterL, parameterM];
}
